import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Trash2, Move } from "lucide-react";

/**
 * VideoStage
 * ----------
 * - Carga el video local via URL.createObjectURL (sin upload).
 * - Mantiene aspect ratio del video original en el contenedor.
 * - Permite dibujar un rectángulo (ROI) con el mouse.
 * - Calcula coordenadas absolutas relativas al video original (videoWidth/Height).
 *
 * Props:
 *   file: File local (object)
 *   roi:  { x,y,w,h } absoluto en píxeles del video original (state controlado)
 *   onRoiChange: (newRoi)=>void
 *   onReset: () => void
 */
export default function VideoStage({ file, roi, onRoiChange, onReset }) {
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const [objectUrl, setObjectUrl] = useState("");
  const [dim, setDim] = useState({ w: 0, h: 0 }); // intrinsic video dims
  const [playing, setPlaying] = useState(false);
  const [drawing, setDrawing] = useState(null); // {startX,startY,curX,curY}

  // Generate / revoke object URL
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onLoadedMeta = () => {
    const v = videoRef.current;
    if (!v) return;
    setDim({ w: v.videoWidth, h: v.videoHeight });
    // Pause at first frame to expose it
    v.currentTime = 0;
    v.pause();
    setPlaying(false);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  // Convert client mouse coords -> absolute video coords
  const toVideoCoords = (clientX, clientY) => {
    const wrap = wrapperRef.current;
    if (!wrap || !dim.w || !dim.h) return null;
    const rect = wrap.getBoundingClientRect();
    const relX = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const relY = Math.min(Math.max(clientY - rect.top, 0), rect.height);
    const scaleX = dim.w / rect.width;
    const scaleY = dim.h / rect.height;
    return { x: Math.round(relX * scaleX), y: Math.round(relY * scaleY) };
  };

  const onPointerDown = (e) => {
    if (!dim.w) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = toVideoCoords(e.clientX, e.clientY);
    if (!p) return;
    setDrawing({ startX: p.x, startY: p.y, curX: p.x, curY: p.y });
  };

  const onPointerMove = (e) => {
    if (!drawing) return;
    const p = toVideoCoords(e.clientX, e.clientY);
    if (!p) return;
    setDrawing((d) => ({ ...d, curX: p.x, curY: p.y }));
  };

  const onPointerUp = () => {
    if (!drawing) return;
    const x = Math.min(drawing.startX, drawing.curX);
    const y = Math.min(drawing.startY, drawing.curY);
    const w = Math.abs(drawing.curX - drawing.startX);
    const h = Math.abs(drawing.curY - drawing.startY);
    setDrawing(null);
    if (w >= 4 && h >= 4) {
      onRoiChange({ x, y, w, h });
    }
  };

  // Compute on-screen ROI box (during drawing or stored)
  const getDisplayBox = () => {
    const wrap = wrapperRef.current;
    if (!wrap || !dim.w || !dim.h) return null;
    const rect = wrap.getBoundingClientRect();
    const sx = rect.width / dim.w;
    const sy = rect.height / dim.h;
    let src = null;
    if (drawing) {
      const x = Math.min(drawing.startX, drawing.curX);
      const y = Math.min(drawing.startY, drawing.curY);
      const w = Math.abs(drawing.curX - drawing.startX);
      const h = Math.abs(drawing.curY - drawing.startY);
      src = { x, y, w, h };
    } else if (roi) {
      src = roi;
    }
    if (!src) return null;
    return {
      left: src.x * sx,
      top: src.y * sy,
      width: src.w * sx,
      height: src.h * sy,
    };
  };

  const box = getDisplayBox();

  return (
    <div className="w-full h-full flex flex-col" data-testid="video-stage">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#27272A] bg-[#0A0A0A]">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={togglePlay}
            data-testid="play-toggle"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs uppercase tracking-wider bg-[#1A1A1A] hover:bg-[#27272A] border border-[#27272A] rounded-sm"
          >
            {playing ? (
              <Pause size={13} strokeWidth={1.75} />
            ) : (
              <Play size={13} strokeWidth={1.75} />
            )}
            {playing ? "Pause" : "Play"}
          </button>
          <button
            type="button"
            onClick={() => onRoiChange(null)}
            disabled={!roi}
            data-testid="clear-roi-btn"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs uppercase tracking-wider bg-[#1A1A1A] hover:bg-[#27272A] disabled:opacity-40 disabled:hover:bg-[#1A1A1A] border border-[#27272A] rounded-sm"
          >
            <RotateCcw size={13} strokeWidth={1.75} /> Reset ROI
          </button>
        </div>
        <div className="flex items-center gap-3 font-mono-pro text-[11px] text-[#A1A1AA]">
          <span className="text-[#71717A]">RES</span>
          <span data-testid="video-resolution">
            {dim.w}×{dim.h}
          </span>
          <span className="text-[#71717A] ml-3">FILE</span>
          <span
            className="truncate max-w-[180px]"
            data-testid="video-filename"
            title={file?.name}
          >
            {file?.name}
          </span>
          <button
            type="button"
            onClick={onReset}
            data-testid="discard-file-btn"
            className="ml-3 inline-flex items-center gap-1 text-[#71717A] hover:text-[#E01F26] transition-colors"
          >
            <Trash2 size={13} strokeWidth={1.5} /> Descartar
          </button>
        </div>
      </div>

      {/* Player container */}
      <div className="flex-1 min-h-0 flex items-center justify-center p-6 player-grid bg-[#0A0A0A]">
        {objectUrl && (
          <div
            ref={wrapperRef}
            className="relative bg-black ring-1 ring-[#27272A] shadow-2xl"
            style={{
              aspectRatio: dim.w && dim.h ? `${dim.w} / ${dim.h}` : "16 / 9",
              maxWidth: "100%",
              maxHeight: "100%",
              width: "min(100%, calc((100vh - 220px) * " + (dim.w && dim.h ? dim.w / dim.h : 16 / 9) + "))",
            }}
          >
            <video
              ref={videoRef}
              src={objectUrl}
              onLoadedMetadata={onLoadedMeta}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              className="absolute inset-0 w-full h-full object-contain bg-black"
              playsInline
              muted
              data-testid="video-preview-player"
            />

            {/* ROI canvas overlay */}
            <div
              className="absolute inset-0 roi-canvas"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              data-testid="roi-drawing-canvas"
            >
              {box && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: `${box.left}px`,
                    top: `${box.top}px`,
                    width: `${box.width}px`,
                    height: `${box.height}px`,
                    border: "2px solid #E01F26",
                    background: "rgba(224, 31, 38, 0.12)",
                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.35) inset",
                  }}
                  data-testid="roi-rect"
                >
                  {/* Corner handles */}
                  {["tl", "tr", "bl", "br"].map((c) => {
                    const style = {
                      tl: { top: -5, left: -5 },
                      tr: { top: -5, right: -5 },
                      bl: { bottom: -5, left: -5 },
                      br: { bottom: -5, right: -5 },
                    }[c];
                    return (
                      <span
                        key={c}
                        className="absolute w-2.5 h-2.5 bg-[#FFC82E]"
                        style={style}
                      />
                    );
                  })}
                  {/* Move icon */}
                  <span className="absolute -top-6 left-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#FFC82E] font-mono-pro">
                    <Move size={10} strokeWidth={2} /> ROI
                  </span>
                </div>
              )}

              {!box && (
                <div
                  className="absolute inset-x-0 bottom-6 mx-auto text-center pointer-events-none"
                  data-testid="roi-hint"
                >
                  <span className="inline-block px-3 py-1.5 bg-[#0A0A0A]/80 border border-[#27272A] text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-pro">
                    Dibuja un rectángulo sobre el logo
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
